import { useEffect, useState } from 'react'
import { Monitor } from 'lucide-react'

/**
 * "Get the desktop app" — the only path from the browser build to the installer.
 *
 * Renders nothing in three cases, all of them deliberate:
 *
 *  1. Already running inside the desktop app. Advertising the thing you are
 *     currently using is the kind of detail that makes a product feel unfinished.
 *  2. No published build. The check is the same `latest.json` the updater polls,
 *     which returns 204 for an empty channel — so `published` in the database is
 *     the single switch behind the download page, the update manifest AND this
 *     button. Pulling a release cannot leave a live "download" link behind.
 *  3. The check failed. A dead network should cost a button, not an error.
 */
export default function DesktopPromo() {
  const [href, setHref] = useState<string | null>(null)

  useEffect(() => {
    // Tauri v2 exposes __TAURI_INTERNALS__ on the window it hosts. Checking the
    // window beats sniffing the user agent, which Tauri leaves looking like a
    // normal browser on purpose.
    if (typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window) return

    let alive = true
    fetch('/api/studio/desktop/latest.json')
      .then(r => (r.status === 200 ? r.json() : null))
      .then(j => {
        if (!alive || !j?.platforms?.['windows-x86_64']) return
        setHref('/desktop')
      })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  if (!href) return null

  return (
    <a
      href={href}
      className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg
                 border border-white/15 text-gray-300 hover:text-white hover:bg-white/5
                 transition-colors"
      title="Download legit2cloud Studio for Windows"
    >
      <Monitor size={13} />
      Get the desktop app
    </a>
  )
}
